import { usePage } from "@inertiajs/react";
import { ModalRoot } from "@inertiaui/modal-react";
import { Intercom } from "@intercom/messenger-js-sdk";
import { PageStack } from "@ui/components/Page";
import { Toaster } from "@ui/components/Toast";
import posthog from "posthog-js";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { MessageDock } from "@/components/MessageDock/MessageDock";
import { RoutedModalDeepLink } from "@/components/RoutedModalDeepLink";
import { useFlashToasts } from "@/hooks/use-flash-toasts";
import { useTrackInAppHistory } from "@/hooks/use-navigate-back";
import { usePushNotifications } from "@/hooks/use-push-notifications";
import { ACCOUNT_GROUP } from "@/lib/analytics-events";
import { ConsentProvider } from "@/providers/consent-provider";
import { PostHogProvider } from "@/providers/posthog-provider";
import { PrefetchProvider } from "@/providers/prefetch-provider";
import { RouterProvider } from "@/providers/router-provider";
import type { SharedProps } from "@/types";

interface PersistentLayoutProps {
  children: ReactNode;
}

const intercomAppId = import.meta.env.VITE_INTERCOM_APP_ID;

export default function PersistentLayout({ children }: PersistentLayoutProps) {
  const { current_user, current_account, intercom } = usePage<SharedProps>().props;
  useFlashToasts();
  useTrackInAppHistory();
  usePushNotifications();
  const previousUserId = useRef<number | null>(null);

  useEffect(() => {
    if (!intercomAppId) return;

    // Single boot site for Intercom. The default floating launcher is always
    // hidden — the messenger is opened explicitly via "Get Help" in the account
    // menu and the creator-wizard "Questions?" buttons (see use-intercom.ts).
    // `intercom.user_jwt` is only present for a signed-in user, so it doubles as
    // the auth gate.
    Intercom({
      app_id: intercomAppId,
      hide_default_launcher: true,
      ...(intercom?.user_jwt ? { intercom_user_jwt: intercom.user_jwt } : {}),
    });
  }, [intercom?.user_jwt]);

  useEffect(() => {
    if (current_user) {
      posthog.identify(String(current_user.id), {
        email: current_user.email,
        name: current_user.name,
      });
      // Associate the acting user's own account as a PostHog group so their
      // events roll up at the account level (Group Analytics). Server-side
      // seller events attach the seller account group independently.
      if (current_account) {
        posthog.group(ACCOUNT_GROUP, String(current_account.id));
      }
      previousUserId.current = current_user.id;
    } else if (previousUserId.current !== null) {
      posthog.reset();
      previousUserId.current = null;
    }
  }, [current_user, current_account]);

  return (
    <ConsentProvider>
      <PostHogProvider>
        <RouterProvider>
          <PrefetchProvider>
            {/* The stack must enclose both the parallax target and ModalRoot,
                where pushed Pages mount: Silk resolves stack membership through
                React context, so a Page outside this subtree joins no stack.
                The root renders `display: contents`, so enclosing the dock and
                the toaster costs them no layout box. */}
            <PageStack.Root>
              <PageStack.Outlet>{children}</PageStack.Outlet>
              <MessageDock />
              <Toaster />
              <ModalRoot />
              <RoutedModalDeepLink />
            </PageStack.Root>
          </PrefetchProvider>
        </RouterProvider>
      </PostHogProvider>
    </ConsentProvider>
  );
}
